import React from 'react';
import { Project } from '../types';

interface HeaderProps {
  currentProject: Project | null;
  userProjects: { id: string; name: string; lastEdited: number }[];
  exampleProjects: { id: string; name: string; lastEdited: number }[];
  currentView: string;
  onProjectSelect: (id: string) => void;
  onViewChange: (view: string) => void;
}

export function Header({
  currentProject,
  userProjects,
  exampleProjects,
  currentView,
  onProjectSelect,
  onViewChange,
}: HeaderProps) {
  // Only log project changes when the list actually changes
  React.useEffect(() => {
    console.debug('Projects updated:', {
      user: userProjects.length,
      example: exampleProjects.length
    });
  }, [userProjects, exampleProjects]);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="header-content flex flex-col lg:flex-row lg:items-center h-auto">
        {/* Top row - Logo and Project Selector */}
        <div className="flex items-center justify-between w-full lg:w-auto py-2 lg:py-3 gap-4">
          <div className="text-base lg:text-xl font-medium text-[#4A4538] whitespace-nowrap ml-4 lg:ml-[16px] shrink-0">Rail Mapper</div>

          {/* Project Selector - Next to logo on mobile/tablet, with rest of nav on desktop */}
          <div className="relative lg:hidden min-w-[200px] max-w-[300px] flex-1 mr-4">
            <select
              value={currentProject?.id || 'new'}
              onChange={(e) => {
                onProjectSelect(e.target.value);
              }}
              className="w-full py-1.5 px-2 border rounded appearance-none pr-6 text-sm truncate"
            >
              <optgroup label="Your Projects">
                <option value="new">Add new project</option>
                {userProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Example Projects">
                {exampleProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </optgroup>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
              <svg className="h-3 w-3 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Desktop Project Selector and Navigation */}
        <div className="lg:ml-auto flex flex-col lg:flex-row lg:items-center lg:gap-12 mr-4 lg:mr-[16px]">
          {/* Project Selector - Desktop only */}
          <div className="hidden lg:block relative shrink-0">
            <select
              value={currentProject?.id || 'new'}
              onChange={(e) => {
                onProjectSelect(e.target.value);
              }}
              className="p-2 border rounded appearance-none pr-8 w-[300px] text-base"
            >
              <optgroup label="Your Projects">
                <option value="new">Add new project</option>
                {userProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({new Date(project.lastEdited).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                  </option>
                ))}
              </optgroup>
              <optgroup label="Example Projects">
                {exampleProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </optgroup>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
              <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="nav-buttons flex items-center justify-center w-full border-t lg:border-t-0 border-gray-100 overflow-x-auto lg:overflow-visible lg:py-3">
            <div className="flex items-center gap-1 flex-nowrap px-2 lg:px-[16px] py-1.5 lg:py-0">
              <button
                onClick={() => onViewChange('network')}
                className={`nav-button h-7 lg:h-10 px-2 lg:px-3 flex items-center gap-1 rounded text-xs lg:text-sm whitespace-nowrap ${
                  currentView === 'network' ? 'bg-[#6A8CAF] text-white' : 'text-[#4A4538] hover:bg-gray-100'
                }`}
              >
                Network
              </button>
              <button
                onClick={() => {
                  onViewChange('config');
                }}
                className={`nav-button h-7 lg:h-10 px-2 lg:px-3 flex items-center gap-1 rounded text-xs lg:text-sm whitespace-nowrap ${
                  currentView === 'config' ? 'bg-[#6A8CAF] text-white' : 'text-[#4A4538] hover:bg-gray-100'
                }`}
              >
                Config
              </button>
              <button
                onClick={() => onViewChange('results')}
                className={`nav-button h-7 lg:h-10 px-2 lg:px-3 flex items-center gap-1 rounded text-xs lg:text-sm whitespace-nowrap ${
                  currentView === 'results' ? 'bg-[#6A8CAF] text-white' : 'text-[#4A4538] hover:bg-gray-100'
                }`}
              >
                Results
              </button>
              <button
                onClick={() => onViewChange('summary')}
                className={`nav-button h-7 lg:h-10 px-2 lg:px-3 flex items-center gap-1 rounded text-xs lg:text-sm whitespace-nowrap ${
                  currentView === 'summary' ? 'bg-[#6A8CAF] text-white' : 'text-[#4A4538] hover:bg-gray-100'
                }`}
              >
                Summary
              </button>
              <button
                onClick={() => onViewChange('export')}
                className={`nav-button h-7 lg:h-10 px-2 lg:px-3 flex items-center gap-1 rounded text-xs lg:text-sm whitespace-nowrap ${
                  currentView === 'export' ? 'bg-[#6A8CAF] text-white' : 'text-[#4A4538] hover:bg-gray-100'
                }`}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
} 